import {
    Box,
    Button,
    CircularProgress,
    Container,
    Grid,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import Navbar from "./navbar";
import Slider from "./slider";

import IdentityOverview from "./identityOverview";
import IdentitySlider from "./identitySlider";
import MotivatorOverview from "./motivatorOverview";

import CloudDownload from "@mui/icons-material/CloudDownload";
import Search from "@mui/icons-material/Search";
import useText, { TextContextProvider } from "../../hooks/useText";
import IdentityFlash from "./identityFlash";
import VideoPopoverTrigger from "./VideoPopoverTrigger";

const Anchor = ({ id }: { id: string }) => (
    <div id={id + "Spy"} style={{ position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: "-145px" }} id={id} />
    </div>
);
interface ResultsBodyProps {
    name: string;
    identityData: any[];
    motivatorData: any[];
    downloadUrl: string;
    onBack?: VoidFunction;
}

interface Api {
    getAssessment: (assessmentId: number) => Promise<any>;
    downloadReport: (assessmentId: number) => Promise<any>;
}

interface ResultsControllerProps {

    assessmentId: number;
    onBack: VoidFunction;
    api: Api
}

export const ResultsBody: React.FC<ResultsBodyProps> = ({
    name,
    identityData,
    motivatorData,
    downloadUrl,
    onBack,
}) => {
    const [tab, setTab] = useState(0);
    const theme = useTheme();
    const text = useText()?.index;

    const motivatorOverviewTitles = [
        {
            background: theme.palette.background.default,
            title: text.motivatorOverview.strong,
            secondary: text.motivatorOverview.strongOverview,
        },
        {
            background: theme.palette.background.default,
            title: text.motivatorOverview.moderate,
            secondary: text.motivatorOverview.moderateOverview,
        },
        {
            background: theme.palette.background.default,
            title: text.motivatorOverview.neutral,
            secondary: text.motivatorOverview.neutralOverview,
        },
    ];

    return (
        <>
            <Navbar
                tabs={[
                    {
                        label: text.navTabs.welcome,
                        href: "welcome",
                    },
                    {
                        label: text.navTabs.motivators,
                        href: "motivators",
                    },
                    {
                        label: text.navTabs.identities,
                        href: "identities",
                    },
                ]}
                tabValue={tab}
                tabHandler={(newVal) => setTab(newVal)}
                downloadUrl={downloadUrl}
                onBack={onBack}
            />
            <Box
                sx={{
                    pt: "97px",
                }}
            />
            <Anchor id="welcome" />
            <Slider sections={text.welcomeSlider.sections}>
                <Box
                    sx={{
                        p: (t) => t.spacing(5, 5),
                    }}
                >
                    <Container>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="center"
                            style={{ minHeight: "60vh" }}
                        >
                            <Grid item sm={6} xs={12}>
                                <Grid container alignItems="center" style={{ height: "100%" }}>
                                    <Grid item style={{ height: "100%" }}>
                                        <Typography variant="h3" color="secondary">
                                            {text.welcomeSlider.welcome.replace("BOB", name)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {text.welcomeSlider.welcomeText}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item sm={6} xs={12} style={{ textAlign: "center" }}>
                                <div>
                                    <Button
                                        style={{ margin: theme.spacing(2) }}
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        startIcon={<CloudDownload />}
                                        component="a"
                                        href={downloadUrl}
                                        target="_blank"
                                    >
                                        {text.welcomeSlider.download}
                                    </Button>
                                    <VideoPopoverTrigger src="/report/video/Welcome.mp4" />
                                </div>
                                <div style={{ marginTop: theme.spacing(2) }}>
                                    <Button
                                        href="#motivators"
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        startIcon={<Search />}
                                    >
                                        {text.welcomeSlider.explore}
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
                <Box
                    sx={{
                        backgroundColor: "white",
                        backgroundSize: "cover",
                        padding: (theme) => theme.spacing(5, 5),
                        minHeight: "70vh",
                    }}
                >
                    <Container>
                        <Grid container alignItems="center">
                            <Grid item sm={4} xs={12}>
                                <Typography variant="body1">
                                    <strong>{text.welcomeSlider.motivators}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    {text.welcomeSlider.motivatorsExplanation}
                                </Typography>
                                <br />
                                <Typography variant="body1">
                                    <strong>{text.welcomeSlider.identities}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    {text.welcomeSlider.identitiesExplanation}
                                </Typography>
                            </Grid>
                            <Grid item sm={6} xs={12} style={{ textAlign: "center" }}>
                                <img src="/images/IconWheel.png" alt="Identity Explanation" />
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
                <Box
                    sx={{
                        backgroundColor: "white",
                        backgroundSize: "cover",
                        padding: (theme) => theme.spacing(5, 5),
                        minHeight: "70vh",
                    }}
                >
                    <Container>
                        <Grid container>
                            <Grid item sm={4} xs={12}>
                                <Typography variant="body1">
                                    <strong>{text.welcomeSlider.motivatorCluser}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    {text.welcomeSlider.motivatorClusterExplanation}
                                </Typography>
                            </Grid>
                            <Grid item sm={8} xs={12}>
                                <Grid container gap={2} justifyContent="space-between" flexWrap='wrap'>
                                    {[...identityData]
                                        .sort((a, b) => (a.identity > b.identity ? 1 : -1))
                                        .map((i, index) => (
                                            <Grid key={"Identity" + index} item>
                                                <IdentityFlash identityKey={i.key} {...i} />
                                            </Grid>
                                        ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            </Slider>
            <Anchor id="motivators" />
            <Slider sections={text.motivatorSlider.sections}>
                {motivatorData
                    .reduce<[any[], any[], any[]]>(
                        (acc, next, index) => {
                            if (index < 7) acc[0].push({ ...next, index });
                            else if (index < 14) acc[1].push({ ...next, index });
                            else acc[2].push({ ...next, index });

                            return acc;
                        },
                        [[], [], []]
                    )
                    .map((motivators, key) => (
                        <MotivatorOverview
                            key={key}
                            motivators={motivators}
                            title={motivatorOverviewTitles[key].title}
                            secondaryTitle={motivatorOverviewTitles[key].secondary}
                            backgroundColor={motivatorOverviewTitles[key].background}
                        />
                    ))}
            </Slider>
            <Anchor id="identities" />
            <IdentityOverview identities={identityData} />
            {identityData
                .filter((_, i) => i < 4)
                .map((a, i) => (
                    <IdentitySlider key={"slider" + i} identityKey={a.key} {...a} />
                ))}
        </>
    );
};


const ResultsController: React.FC<ResultsControllerProps> = ({
    assessmentId,
    onBack,
    api,
}) => {
    const [name, setName] = useState("");
    const [identityData, setIdentityData] = useState([]);
    const [motivatorData, setMotivatorData] = useState([]);
    const [downloadURL, setDownloadURL] = useState("");
    const [text, setText] = useState(null);



    useEffect(() => {
        if (!assessmentId) return;
        api.getAssessment(assessmentId).then((res: any) => {
            // eslint-disable-next-line no-prototype-builtins
            if (res.hasOwnProperty("error")) return alert(res.error);
            setName(res.name || "");
            setIdentityData(
                res.identityOverview.map((a: any, i: number) => ({
                    ...a,
                    identityRank: i + 1,
                }))
            );
            setMotivatorData(res.motivators);
            setText(res.text);
        });
        api
            .downloadReport(assessmentId)
            .then(({ data: { url } }: any) => setDownloadURL(url));
    }, [assessmentId]);
    return (
        <TextContextProvider value={text}>
            {!text ? (
                <CircularProgress />
            ) : (
                <ResultsBody
                    onBack={onBack}
                    name={name}
                    identityData={identityData}
                    motivatorData={motivatorData}
                    downloadUrl={downloadURL}
                />
            )}
        </TextContextProvider>
    );
};

export default ResultsController;